import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type { ReleaseDetail, ReleaseObservation } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Select } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";

/** §41's usual windows. Anything longer is set per release; these cover the common cases. */
const WINDOWS: [string, string][] = [
  ["", "No window — complete as soon as the checks pass"],
  ["30", "30 minutes"],
  ["60", "1 hour"],
  ["120", "2 hours"],
  ["360", "6 hours"],
  ["1440", "24 hours"],
];

/**
 * How long the release is watched before it can be called finished (§41).
 *
 * <p>Worth setting for anything going to production. Most incidents surface in the gap between
 * "the deployment finished" and "somebody looked properly", and a window is what stops a release
 * being signed off inside that gap.
 */
export function ObservationDrawer({
  release,
  observation,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  observation: ReleaseObservation | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [minutes, setMinutes] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (open) {
      setMinutes(observation?.observationMinutes
        ? String(observation.observationMinutes) : "");
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, observation]);

  const submit = () =>
    void run(
      () =>
        api.put(`/api/v1/releases/${release.id}/observation`, {
          observationMinutes: minutes === "" ? null : Number(minutes),
          lockVersion: release.lockVersion,
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Observation window"
      subtitle={`${release.releaseKey} — ${release.releaseName}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <p className="text-sm text-ink-600">
          After the checks pass, the release stays in verification for this long before it can be
          completed. Most incidents surface in the gap between a deployment finishing and somebody
          looking properly.
        </p>

        <Field label="Watch for" required>
          <Select value={minutes} onChange={(event) => setMinutes(event.target.value)}>
            {WINDOWS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        {observation?.verificationStartedAt && (
          <p className="rounded-md bg-ink-50 px-3 py-2 text-xs text-ink-700">
            Verification began {new Date(observation.verificationStartedAt).toLocaleString()}.
            Changing the window moves when this release can be completed.
          </p>
        )}
      </div>
    </Drawer>
  );
}
